import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useRef,
  useMemo,
} from 'react';
import { WorkflowDetails } from '../../axiosApi/models/workflow-details';
import Api from '../../axiosApi/api';
import { useApi } from '../../api/ApiProvider';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';

interface WorkflowContextProps {
  workflow: WorkflowDetails | null;
  loading: boolean;
  errors: Error | null;
}

const WorkflowContext = createContext<WorkflowContextProps | undefined>(undefined);

interface WorkflowProviderProps {
  moduleId: number;
  children: ReactNode;
}

// Global cache shared across all WorkflowProvider instances
const workflowCache = new Map<number, WorkflowDetails>();
// Track ongoing fetches to prevent duplicate requests
const ongoingFetches = new Map<number, Promise<void>>();

export const WorkflowProvider: React.FC<WorkflowProviderProps> = ({ moduleId, children }) => {
  const [workflow, setWorkflow] = useState<WorkflowDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errors, setErrors] = useState<Error | null>(null);

  const api: Api = useApi();
  const toast = useToastMessageQueue();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!moduleId) return;

    const fetchWorkflow = async () => {
      // Reset state for the new moduleId
      setLoading(true);
      setErrors(null);

      // Check cache first
      if (workflowCache.has(moduleId)) {
        setWorkflow(workflowCache.get(moduleId) || null);
        setLoading(false);
        return;
      }

      // Check if there's already an ongoing fetch for this moduleId
      if (ongoingFetches.has(moduleId)) {
        await ongoingFetches.get(moduleId);
        // After the ongoing fetch completes, set the workflow from cache
        if (isMountedRef.current && workflowCache.has(moduleId)) {
          setWorkflow(workflowCache.get(moduleId) || null);
          setLoading(false);
        }
        return;
      }

      // Create a new fetch promise
      const fetchPromise = (async () => {
        try {
          const response = await api.workflowApi.apiVversionWorkflowModuleIdActiveScopedGet(
            moduleId,
            '1',
            {}
          );

          if (response.data?.data) {
            workflowCache.set(moduleId, response.data.data);
            if (isMountedRef.current) {
              setWorkflow(response.data.data);
            }
          } else {
            if (isMountedRef.current) {
              setWorkflow(null);
            }
          }
        } catch (error) {
          if (isMountedRef.current) {
            handleAPIError(error, toast, {});
            setErrors(error);
          }
        } finally {
          ongoingFetches.delete(moduleId);
          if (isMountedRef.current) {
            setLoading(false);
          }
        }
      })();

      ongoingFetches.set(moduleId, fetchPromise);
      await fetchPromise;
    };

    fetchWorkflow();
  }, [moduleId, api, toast]);

  const contextValue = useMemo(
    () => ({
      workflow,
      loading,
      errors,
    }),
    [workflow, loading, errors]
  );

  return <WorkflowContext.Provider value={contextValue}>{children}</WorkflowContext.Provider>;
};

export const useWorkflow = (): WorkflowContextProps => {
  const context = useContext(WorkflowContext);
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
};
